import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';

/**
 * Interface for default question
 */
// export interface Question {
//   id: number;
//   type: string;
//   question?: string;
//   text?: string;
//   options?: { label: string; value: string }[];
//   scale_max?: number;
//   scale_min?: number;
//   calculation_info?: { red_flags: string[]; ideal_keywords: string[] };
// }

interface Option {
  label: string;
  value: string;
  score?: number;
}

interface Question {
  id: number;
  type: 'ranking' | 'multiple_choice' | 'scale';
  question?: string;
  text?: string;
  options?: Option[];
  scale_max?: number;
  scale_min?: number;
}

interface QuestionsByLevel {
  mid: Question[];
  entry: Question[];
  senior: Question[];
}

interface CompetencyQuestions {
  questions: QuestionsByLevel;
  competency: string;
}

interface MotivatorQuestions {
  motivator: string;
  questions: Question[];
  description: string;
}

interface ScoringLogic {
  max_score: number;
  percentages: {
    [key: string]: number;
  };
  matching_rule: string;
  match_per_option: number;
}

interface CalculationInfo {
  red_flags: string[];
  ideal_keywords: string[];
}

interface SoftCompetency {
  questions: {
    question: string;
    calculation_info: CalculationInfo;
  }[];
  competency: string;
}

interface BasicExpectationsMetric {
  label: string;
  points: number;
  match_type: string;
  note?: string;
}

interface BasicExpectationsQuestion {
  text: string;
  type: string;
  metrics: BasicExpectationsMetric[];
  calculation: string;
  condition?: string;
}

interface NumberFormatting {
  hourly_rate: string[];
  project_rate: string[];
  monthly_salary: string[];
}

interface BasicExpectations {
  scoring: {
    rules: string[];
    default_total: number;
    with_location_total: number;
  };
  questions: BasicExpectationsQuestion[];
  competence: string;
  location_notes: {
    axis: string;
    city_state: string;
    country_source: string;
  };
  number_formatting: NumberFormatting;
  output_format_note: string;
}

interface Personality {
  text: string;
  type: any;
  number: number;
  options: string[];
  category: string;
  instruction: string;
  rating_scale: {
    max: number;
    min: number;
  };
  correct_order?: string[];
  items?: string[];
}

interface WorkplaceQuality {
  quality: string;
  questions: Question[];
  description: string;
}

interface WorkStyle {
  candidate: {
    questions: CompetencyQuestions[];
  };
}

interface WorkMotivators {
  candidate: {
    questions: MotivatorQuestions[];
  };
  scoring_logic: ScoringLogic;
}

interface QuestionsData {
  work_style: WorkStyle;
  work_motivators: WorkMotivators;
  soft_competencies: SoftCompetency[];
  basic_expectations: BasicExpectations;
  workplace_qualities: WorkplaceQuality[];
  personality: Personality;
  domain_knowledge?: any; // Domain knowledge questions
}

interface DataItem {
  id: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  questions: QuestionsData;
  assessment: number;
}

interface ResponseData {
  message: string;
  data: DataItem[];
}

/**
 * Hook for fetching default questions using TanStack React Query
 */
export const useGetDefaultQuestions = (assessmentId: string | null) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['defaultQuestions', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<ResponseData>('/candidate/default-questions/', {
        params: { assessment: assessmentId },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!assessmentId,
  });

  return {
    defaultQuestions: data?.data || [],
    defaultQuestionsResponse: data,
    isLoading,
    error,
    refetchDefaultQuestions: () => queryClient.invalidateQueries({ queryKey: ['defaultQuestions', assessmentId] }),
  };
};